import { CloseOutlined } from '@ant-design/icons';
import { useState } from 'react';
import styled from 'styled-components';

import { Flex, Space } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Input from '@/components/Inputs/Input';
import PasswordInput from '@/components/Inputs/PasswordInput';
import { useModalContext } from '@/components/Modal/Modal';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { modalStyle } from '@/styles/style';

import { IMPORT_STATE, type StampedInfo } from '../types';

type Props = {
  onConnectComplete: (workspace: StampedInfo) => void;
};

const StampedTokenModal = (props: Props) => {
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const color = useColor();
  const { closeModal } = useModalContext();

  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [storeHash, setStoreHash] = useState('');
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const onConnectClick = async () => {
    if (
      apiKey.trim() === '' ||
      apiSecret.trim() === '' ||
      storeHash.trim() === ''
    )
      return;
    if (!currWorkspace) return;
    const data = await service.stamped.createStampedInfo(
      apiKey,
      apiSecret,
      storeHash,
      currWorkspace.alias,
    );
    if (data) {
      props.onConnectComplete({
        workspace_id: data.workspace_id || data.alias,
        alias: data.alias,
        import_state:
          data.import_state || data.state || IMPORT_STATE.NOT_STARTED,
        history: data.history || [],
      });
      setConnectionError(null);
      closeModal();
    } else {
      setConnectionError('Your access token is invalid. Please check again!');
    }
  };

  return (
    <ModalStyle d="column">
      <Flex j="space-between">
        <Heading type="h2">Stamped Integration</Heading>
        <IconButton
          icon={<CloseOutlined />}
          outlined={false}
          sizeType="l"
          onClick={closeModal}
        />
      </Flex>
      <Space h="24px" />
      <Typo type="label2" color={color.gray700}>
        Store hash, api key and api secret are required to connect to Stamped.
      </Typo>
      <Space h="16px" />
      <Heading type="h3">Store hash</Heading>
      <Space h="12px" />
      <Input
        placeholder="type store hash"
        sizeType="m"
        value={storeHash}
        onChange={(e) => setStoreHash(e.target.value)}
      />
      <Space h="24px" />
      <Heading type="h3">Public key</Heading>
      <Space h="12px" />
      <Input
        placeholder="type public key"
        sizeType="m"
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
      />
      <Space h="24px" />
      <Heading type="h3">Private key</Heading>
      <Space h="12px" />
      <PasswordInput
        sizeType="m"
        placeholder="type private key"
        name="apiSecret"
        value={apiSecret}
        onChange={(e) => setApiSecret(e.target.value)}
      />
      <Space h="32px" />
      {connectionError ? (
        <Flex j="space-between" a="center">
          <Typo type="label2" style={{ color: color.red600 }}>
            {connectionError}
          </Typo>
          <Button
            sizeType="m"
            onClick={onConnectClick}
            disabled={
              apiKey.trim() === '' ||
              apiSecret.trim() === '' ||
              storeHash.trim() === ''
            }
      >
            Connect
          </Button>
        </Flex>
      ) : (
        <Flex j="flex-end">
          <Button
            sizeType="m"
            onClick={onConnectClick}
            disabled={
              apiKey.trim() === '' ||
              apiSecret.trim() === '' ||
              storeHash.trim() === ''
            }
          >
            Connect
          </Button>
        </Flex>
      )}
    </ModalStyle>
  );
};

const ModalStyle = styled(Flex)`
  width: 620px;
  padding: 32px;
  color: ${(props) => props.theme.colors.gray800};
  ${modalStyle};
`;

export default StampedTokenModal;
